import localforage from 'myStore'
import queryString from 'query-string'

import { getEnvParam } from 'env'
import { updateToken, handleTokenRevoked, handleRateLimited, handleBizAccessBlocked } from '../jwt'
import { isBizErrorCode, BIZ_ERROR_CODES_ENABLED } from '../bizError'

// Type definitions
export interface RestClientOptions {
  tokenKey?: string
  tokenTypeKey?: string
  getAuthFunc?: (token: string) => Promise<{ key: string; value: string }>
  onCallError?: (err: Error) => void
}

export interface RestClientResponse<T = any> {
  origin: Response
  data: T
}

// HTTP 상태 코드를 보존하는 에러. processResponse가 4xx/5xx 응답을 reject할 때 사용.
// 기존 호출자는 err.message만 사용하므로 status 추가는 비파괴적.
export class RestClientError extends Error {
  readonly status: number
  // 응답 body 의 code (biz 710/711/712, 기존 494 등). HTTP status 와 별개로 보존. (OMD-27)
  readonly code?: number

  constructor(message: string, status: number, code?: number) {
    super(message)
    this.name = 'RestClientError'
    this.status = status
    this.code = code
  }
}

export interface XHRResponse<T = any> {
  status: number
  body: any
  json: T
  data: T
  event: ProgressEvent
  xhr: XMLHttpRequest
}

type FetchFunction = typeof fetch

class RestClient {
  // [fix] fetch/XHR 요청에 타임아웃이 없어 네트워크 hang 시 Promise가 영원히 settle되지 않는 문제 방지.
  // fetch: AbortController로 60초 후 요청 강제 중단, XHR: xhr.timeout으로 5분 후 중단.
  static readonly FETCH_TIMEOUT_MS = 60_000
  static readonly XHR_TIMEOUT_MS = 300_000

  private apiURL: string
  private fetch: FetchFunction
  private tokenKey: string | null = null
  private tokenTypeKey: string | null = null
  private getAuthFunc: ((token: string) => Promise<{ key: string; value: string }>) | null = null
  private withWorkspaceId: boolean = false
  private onCallError?: (err: Error) => void

  constructor(
    apiURL: string,
    fetchFunc: FetchFunction,
    options?: string | RestClientOptions | null,
    getAuthFunc0?: ((token: string) => Promise<{ key: string; value: string }>) | null,
    withWorkspaceId: boolean = false,
  ) {
    this.apiURL = apiURL
    this.fetch = fetchFunc
    this.withWorkspaceId = withWorkspaceId

    let getAuthFunc = getAuthFunc0
    if (options != null) {
      if (typeof options === 'string') {
        this.tokenKey = options
      } else {
        this.tokenKey = options.tokenKey || null
        this.tokenTypeKey = options.tokenTypeKey || null
        if (options.getAuthFunc != null) {
          getAuthFunc = options.getAuthFunc
        }
        this.onCallError = options.onCallError
      }
    }
    this.getAuthFunc = getAuthFunc || null
  }

  xhrBlobType = (xhr: XMLHttpRequest): void => {
    xhr.responseType = 'blob'
  }

  private async applyTokenForOptions(options: RequestInit): Promise<boolean> {
    if (this.tokenKey != null) {
      const token = await localforage.getItem<string>(this.tokenKey)

      let tokenType = 'JWT'
      if (this.tokenTypeKey != null) {
        const storedTokenType = await localforage.getItem<string>(this.tokenTypeKey)
        if (storedTokenType != null) {
          tokenType = storedTokenType
        }
      }

      if (token != null) {
        if (options.headers == null) {
          options.headers = {}
        }
        const headers = options.headers as Record<string, string>
        if (this.getAuthFunc == null) {
          headers.Authorization = `${tokenType} ${token}`
        } else {
          const { key, value } = await this.getAuthFunc(token)
          headers[key] = value
        }
      }
    }
    return Promise.resolve(true)
  }

  private async applyTokenForXHR(xhr: XMLHttpRequest): Promise<boolean> {
    if (this.tokenKey != null) {
      const token = await localforage.getItem<string>(this.tokenKey)

      let tokenType = 'JWT'
      if (this.tokenTypeKey != null) {
        const storedTokenType = await localforage.getItem<string>(this.tokenTypeKey)
        if (storedTokenType != null) {
          tokenType = storedTokenType
        }
      }

      if (token != null) {
        if (this.getAuthFunc == null) {
          xhr.setRequestHeader('Authorization', `${tokenType} ${token}`)
        } else {
          const { key, value } = await this.getAuthFunc(token)
          xhr.setRequestHeader(key, value)
        }
      }
    }
    return Promise.resolve(true)
  }

  private createFetchWithTimeout(
    api: string,
    options: RequestInit,
    timeoutMs: number = RestClient.FETCH_TIMEOUT_MS,
  ): { promise: Promise<Response>; cleanup: () => void } {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs)

    if (options.signal) {
      options.signal.addEventListener('abort', () => controller.abort(), { once: true })
    }
    options.signal = controller.signal

    const promise = this.fetch.call(window, api, options)
    const cleanup = () => clearTimeout(timeoutId)
    return { promise, cleanup }
  }

  private async processResponse<T = any>(
    reqPromise: Promise<Response>,
    hasBody: boolean = true,
    cleanup?: () => void,
  ): Promise<RestClientResponse<T>> {
    return new Promise((resolve, reject) => {
      reqPromise
        .then(async (response) => {
          const code = response.status
          const contentType = response.headers.get('content-type') || ''
          const data =
            contentType.toLowerCase().indexOf('text') !== 0 &&
            hasBody !== false &&
            code !== 204 &&
            code !== 304
              ? await response.json()
              : {}
          if (code >= 200 && code < 400) {
            resolve({
              origin: response,
              data,
            })
          } else {
            const message: string = data?.message || data?.detail || ''
            const bodyCode: number | undefined =
              typeof data?.code === 'number' ? data.code : undefined
            // biz 플랜 계약/권한 상실(HTTP 494 + body code 710/711/712/713/494) — 강제 로그아웃. (OMD-27)
            // (494 body code 는 711 과 동일 문구로 처리) 토큰 revoke / 429 등 기존 분기보다 먼저 처리하고
            // 즉시 reject(catch 체인 보존). 환경 게이트로 감싸 운영/스테이징 빌드에서는 정적 제거된다.
            if (BIZ_ERROR_CODES_ENABLED && code === 494 && isBizErrorCode(bodyCode)) {
              handleBizAccessBlocked(bodyCode as number)
              reject(new RestClientError(message, code, bodyCode))
              return
            }
            // 토큰 revoke / 인증 만료 — 자동 로그아웃 플로우 진입.
            // - 401: customFetch가 refresh 시도 후에도 401이 도달하면 여기서 처리
            // - 403은 메시지 패턴이 토큰 관련일 때만 처리 (정상 권한 부족 403은 그대로 reject)
            // 호출자 흐름은 reject로 통지하여 catch 체인 보존.
            const isTokenRevokeMessage =
              /token has been revoked|invalid token|jwt expired|signature has expired/i.test(
                message,
              )
            if (code === 401 || isTokenRevokeMessage) {
              handleTokenRevoked(`status=${code} message=${message}`)
            }
            // 요청 한도 초과(429) — 사용자에게 안내(debounce). 일시적 제한이므로 reject 흐름은 유지.
            if (code === 429) {
              handleRateLimited(message)
            }
            reject(new RestClientError(message, code, bodyCode))
          }
        })
        .catch((error) => {
          if (this.onCallError) {
            this.onCallError(error)
          }
          reject(error)
        })
        .finally(() => {
          if (cleanup) {
            cleanup()
          }
        })
    })
  }

  async get<T = any>(
    url: string,
    params?: Record<string, any>,
    opts?: RequestInit,
  ): Promise<RestClientResponse<T>> {
    let api = this.apiURL + url

    if (params != null) {
      const search = queryString.stringify(params)
      if (search != null && search.trim().length > 0) {
        api += `?${search}`
      }
    }

    const options: RequestInit = {
      method: 'GET',
      ...opts,
    }

    await this.applyTokenForOptions(options)

    if (this.withWorkspaceId) {
      if (options.headers == null) {
        options.headers = {}
      }
      const headers = options.headers as Record<string, string>

      try {
        const workspaceId = await localforage.getItem<string>('app.user.workspace_id')
        const userEmail = getEnvParam('userEmail') as string

        if (workspaceId != null && userEmail != null) {
          headers['workspace-Id'] = workspaceId
          headers['Email'] = userEmail
        }
      } catch (error) {
        console.warn('Failed to get workspace-Id from localStorage:', error)
      }
    }

    const { promise, cleanup } = this.createFetchWithTimeout(api, options)

    return this.processResponse<T>(promise, true, cleanup)
  }

  async head<T = any>(
    url: string,
    params?: Record<string, any>,
    opts?: RequestInit,
  ): Promise<RestClientResponse<T>> {
    let api = this.apiURL + url

    if (params != null) {
      const search = queryString.stringify(params)
      if (search != null && search.trim().length > 0) {
        api += `?${search}`
      }
    }

    const options: RequestInit = {
      method: 'HEAD',
      ...opts,
    }

    await this.applyTokenForOptions(options)

    const { promise, cleanup } = this.createFetchWithTimeout(api, options)

    return this.processResponse<T>(promise, false, cleanup)
  }

  async post<T = any>(
    url: string,
    params?: Record<string, any>,
    opts?: RequestInit,
  ): Promise<RestClientResponse<T>> {
    const api = this.apiURL + url

    const options: RequestInit = {
      method: 'POST',
      body: JSON.stringify(params),
      ...opts,
    }

    if (options.headers == null) {
      options.headers = {}
    }
    const headers = options.headers as Record<string, string>
    headers['Content-Type'] = 'application/json'

    await this.applyTokenForOptions(options)

    const { promise, cleanup } = this.createFetchWithTimeout(api, options)

    return this.processResponse<T>(promise, true, cleanup)
  }

  async postFormData<T = any>(
    url: string,
    formData: FormData,
    opts?: RequestInit,
  ): Promise<RestClientResponse<T>> {
    const api = this.apiURL + url

    const options: RequestInit = {
      method: 'POST',
      body: formData,
      ...opts,
    }

    if (options.headers == null) {
      options.headers = {}
    }

    await this.applyTokenForOptions(options)

    const { promise, cleanup } = this.createFetchWithTimeout(api, options)

    return this.processResponse<T>(promise, true, cleanup)
  }

  async put<T = any>(
    url: string,
    params?: Record<string, any>,
    opts?: RequestInit,
  ): Promise<RestClientResponse<T>> {
    const api = this.apiURL + url

    const options: RequestInit = {
      method: 'PUT',
      body: JSON.stringify(params),
      ...opts,
    }

    if (options.headers == null) {
      options.headers = {}
    }
    const headers = options.headers as Record<string, string>
    headers['Content-Type'] = 'application/json'

    await this.applyTokenForOptions(options)

    const { promise, cleanup } = this.createFetchWithTimeout(api, options)

    return this.processResponse<T>(promise, true, cleanup)
  }

  async putFormData<T = any>(
    url: string,
    formData: FormData,
    opts?: RequestInit,
  ): Promise<RestClientResponse<T>> {
    const api = this.apiURL + url

    const options: RequestInit = {
      method: 'PUT',
      body: formData,
      ...opts,
    }

    if (options.headers == null) {
      options.headers = {}
    }

    await this.applyTokenForOptions(options)

    const { promise, cleanup } = this.createFetchWithTimeout(api, options)

    return this.processResponse<T>(promise, true, cleanup)
  }

  async patch<T = any>(
    url: string,
    params?: Record<string, any>,
    opts?: RequestInit,
  ): Promise<RestClientResponse<T>> {
    const api = this.apiURL + url

    const options: RequestInit = {
      method: 'PATCH',
      body: JSON.stringify(params),
      ...opts,
    }

    if (options.headers == null) {
      options.headers = {}
    }
    const headers = options.headers as Record<string, string>
    headers['Content-Type'] = 'application/json'

    await this.applyTokenForOptions(options)

    const { promise, cleanup } = this.createFetchWithTimeout(api, options)

    return this.processResponse<T>(promise, true, cleanup)
  }

  async patchFormData<T = any>(
    url: string,
    formData: FormData,
    opts?: RequestInit,
  ): Promise<RestClientResponse<T>> {
    const api = this.apiURL + url

    const options: RequestInit = {
      method: 'PATCH',
      body: formData,
      ...opts,
    }

    if (options.headers == null) {
      options.headers = {}
    }

    await this.applyTokenForOptions(options)

    const { promise, cleanup } = this.createFetchWithTimeout(api, options)

    return this.processResponse<T>(promise, true, cleanup)
  }

  async delete<T = any>(
    url: string,
    params?: Record<string, any>,
    opts?: RequestInit,
  ): Promise<RestClientResponse<T>> {
    const api = this.apiURL + url

    const options: RequestInit = {
      method: 'DELETE',
      body: JSON.stringify(params),
      ...opts,
    }

    if (options.headers == null) {
      options.headers = {}
    }
    const headers = options.headers as Record<string, string>
    headers['Content-Type'] = 'application/json'

    await this.applyTokenForOptions(options)

    const { promise, cleanup } = this.createFetchWithTimeout(api, options)

    return this.processResponse<T>(promise, true, cleanup)
  }

  private processXHR<T = any>(config: {
    method: string
    api: string
    body?: FormData | null
    onProgress?: ((event: ProgressEvent, percent: number) => void) | null
    progressTarget?: 'download' | 'upload'
    onBeforeSend?: ((xhr: XMLHttpRequest) => void) | null
    opts?: { headers?: Record<string, string> }
  }): Promise<XHRResponse<T>> {
    const {
      method,
      api,
      body,
      onProgress,
      progressTarget = 'download',
      onBeforeSend,
      opts,
    } = config

    const promise = new Promise<XHRResponse<T>>(async (resolve, reject) => {
      const xhr = new XMLHttpRequest()

      const onSuccess = (resData) => {
        resolve(resData)
      }

      const onFail = (err) => {
        reject(err)
      }

      if (onProgress != null) {
        const target = progressTarget === 'upload' ? xhr.upload : xhr
        target.addEventListener('progress', (e) => onProgress(e, (e.loaded * 100) / e.total))
      }

      xhr.addEventListener('load', (e) => {
        const { status } = e.target as XMLHttpRequest
        if (status >= 200 && status < 400) {
          let json: any = {}
          const responseBody = xhr.response
          if (
            (xhr.responseType === '' || xhr.responseType === 'text') &&
            xhr.responseText != null &&
            xhr.responseText.charAt(0) === '{'
          ) {
            json = JSON.parse(xhr.responseText)
            // 토큰 만료 패턴 확대: 'Signature has expired' 정확매칭 → 만료 패턴 일반화('jwt expired' 등)
            const msg: string | undefined = json.message
            if (
              json.code === 401 ||
              (msg != null &&
                (msg.indexOf('Signature has expired') > -1 || msg.indexOf('jwt expired') > -1))
            ) {
              callUpdateToken()
              return
            }
          }
          onSuccess({ status: xhr.status, body: responseBody, json, data: json, event: e, xhr })
        } else if (status === 401) {
          callUpdateToken()
        } else {
          onFail((e.target as XMLHttpRequest).response)
        }
      })

      const callUpdateToken = () => {
        updateToken()
          .then(async (token) => {
            // 누락-1: 갱신 실패(null) 시 token 확인 없이 startReq를 재호출하면 401 무한루프.
            // 재시도를 중단하고 onFail로 종료한다. 로그아웃 판정은 failTokenUpdate에 중앙화되어
            // 있으므로 여기서 무조건 로그아웃하지 않는다(일시 네트워크 오류 과잉 처리 방지).
            if (token == null) {
              onFail(new Error('token request failed.'))
              return
            }
            await startReq()
          })
          .catch((err) => {
            onFail(err)
          })
      }

      xhr.timeout = RestClient.XHR_TIMEOUT_MS
      xhr.addEventListener('timeout', () => onFail(new Error('XHR request timeout')))
      xhr.addEventListener('error', (evt) => {
        if (this.onCallError) {
          this.onCallError(new Error('xhr error'))
        }
        onFail(evt)
      })
      xhr.addEventListener('abort', (e) => onFail(e))

      const startReq = async () => {
        xhr.open(method, api, true)

        await this.applyTokenForXHR(xhr)

        if (opts != null && opts.headers != null) {
          Object.entries(opts.headers).forEach(([key, value]) => {
            xhr.setRequestHeader(key, value)
          })
        }

        if (onBeforeSend != null) {
          onBeforeSend(xhr)
        }

        if (body != null) {
          xhr.send(body)
        } else {
          xhr.send()
        }
      }

      await startReq()
    })

    return promise
  }

  xhrGet<T = any>(
    url: string,
    params?: Record<string, any>,
    onProgress?: ((event: ProgressEvent, percent: number) => void) | null,
    onBeforeSend?: ((xhr: XMLHttpRequest) => void) | null,
    opts?: { headers?: Record<string, string> },
  ): Promise<XHRResponse<T>> {
    let api = this.apiURL + url

    if (params != null) {
      const search = queryString.stringify(params)
      if (search != null && search.trim().length > 0) {
        api += `?${search}`
      }
    }

    return this.processXHR<T>({
      method: 'GET',
      api,
      onProgress,
      progressTarget: 'download',
      onBeforeSend,
      opts,
    })
  }

  xhrPost<T = any>(
    url: string,
    formData?: FormData | null,
    onProgress?: ((event: ProgressEvent, percent: number) => void) | null,
    onBeforeSend?: ((xhr: XMLHttpRequest) => void) | null,
    opts?: { headers?: Record<string, string> },
  ): Promise<XHRResponse<T>> {
    return this.processXHR<T>({
      method: 'POST',
      api: this.apiURL + url,
      body: formData,
      onProgress,
      progressTarget: 'upload',
      onBeforeSend,
      opts,
    })
  }

  xhrPut<T = any>(
    url: string,
    formData?: FormData | null,
    onProgress?: ((event: ProgressEvent, percent: number) => void) | null,
    onBeforeSend?: ((xhr: XMLHttpRequest) => void) | null,
    opts?: { headers?: Record<string, string> },
  ): Promise<XHRResponse<T>> {
    return this.processXHR<T>({
      method: 'PUT',
      api: this.apiURL + url,
      body: formData,
      onProgress,
      progressTarget: 'upload',
      onBeforeSend,
      opts,
    })
  }

  xhrPatch<T = any>(
    url: string,
    formData?: FormData | null,
    onProgress?: ((event: ProgressEvent, percent: number) => void) | null,
    onBeforeSend?: ((xhr: XMLHttpRequest) => void) | null,
    opts?: { headers?: Record<string, string> },
  ): Promise<XHRResponse<T>> {
    return this.processXHR<T>({
      method: 'PATCH',
      api: this.apiURL + url,
      body: formData,
      onProgress,
      progressTarget: 'upload',
      onBeforeSend,
      opts,
    })
  }
}

export default RestClient
